import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import ConfirmDialog from "components/admin/ConfirmDialog";
import { describeActionError } from "components/markets/marketActions";
import usePortalAudience from "components/markets/usePortalAudience";
import { apiGet, apiPost } from "lib/apiClient";

const INPUT =
  "mt-1 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30 placeholder:text-muted-foreground";

const CADENCES = [
  { value: "INTERVAL", label: "Short-term", hint: "A new market every few minutes, back to back. Good for fast questions: prices, scores, live events." },
  { value: "DAILY", label: "Every day", hint: "One market a day, open and closing at the same times. Newspaper front pages, daily prices." },
  { value: "WEEKDAYS", label: "Weekdays", hint: "Monday to Friday only." },
  { value: "WEEKLY", label: "Weekly", hint: "Chosen days of the week." },
];
const INTERVALS = [5, 10, 15, 20, 30, 60];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const STATUS_TONE = { ACTIVE: "green", PENDING_APPROVAL: "amber", DRAFT: "gray", PAUSED: "gray", REJECTED: "red", ARCHIVED: "gray" };
const SMALL_BUTTON = "rounded-sm border border-gray-200 px-3 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white";
const ADMIN_ACTIONS = {
  approve: { label: "Approve", title: "Approve this recurring series?", description: "It starts generating markets on its schedule straight away.", tone: "success", done: "approved" },
  reject: { label: "Reject", title: "Reject this recurring series?", description: "Its proposer sees your reason. No markets are generated.", tone: "danger", done: "rejected" },
  archive: { label: "Archive", title: "Archive this recurring series?", description: "It stops generating markets for good. Markets it already created are not affected.", tone: "danger", done: "archived" },
};

const EMPTY = {
  name: "",
  questionTemplate: "",
  outcomes: "Yes",
  resolutionSource: "",
  resolutionRule: "",
  description: "",
  cadence: "INTERVAL",
  intervalMinutes: 5,
  resolutionDelayMinutes: 0,
  daysOfWeek: [1, 2, 3, 4, 5],
  openTimeLocal: "06:00",
  closeTimeLocal: "22:00",
  resolutionTimeLocal: "23:00",
  resolutionOffsetDays: 0,
};

function cadenceText(series) {
  const rule = series.recurrenceRule || {};
  if (series.recurrenceType === "INTERVAL") return `Every ${rule.intervalMinutes} minutes`;
  const window = `${series.timing.openTimeLocal} to ${series.timing.closeTimeLocal}`;
  if (series.recurrenceType === "WEEKLY") return `${(rule.daysOfWeek || []).map((d) => DAYS[d]).join(", ")} · ${window}`;
  return `${series.recurrenceType === "DAILY" ? "Daily" : "Weekdays"} · ${window}`;
}

export default function SeriesPage() {
  const audience = usePortalAudience();
  const canManage = audience.canManage;
  const canApprove = audience.can("market.approve");
  const canArchive = audience.can("series.manage");
  const [pendingAdmin, setPendingAdmin] = useState(null);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const load = useCallback(async () => {
    try {
      const result = await apiGet("/portal/market-series");
      setSeries(result.series || []);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load your recurring markets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const interval = form.cadence === "INTERVAL";
      await apiPost("/portal/market-series", {
        name: form.name.trim(),
        questionTemplate: form.questionTemplate.trim(),
        description: form.description.trim() || null,
        outcomes: form.outcomes.split(/\n|,/).map((o) => o.trim()).filter(Boolean),
        resolutionSource: form.resolutionSource.trim(),
        resolutionRule: form.resolutionRule.trim(),
        cadence: form.cadence,
        ...(interval
          ? { intervalMinutes: Number(form.intervalMinutes), resolutionDelayMinutes: Number(form.resolutionDelayMinutes) || 0 }
          : {
              openTimeLocal: form.openTimeLocal,
              closeTimeLocal: form.closeTimeLocal,
              resolutionTimeLocal: form.resolutionTimeLocal,
              resolutionOffsetDays: Number(form.resolutionOffsetDays) || 0,
              ...(form.cadence === "WEEKLY" ? { daysOfWeek: form.daysOfWeek } : {}),
            }),
      });
      setNotice({
        tone: "green",
        text: audience.isTenant
          ? "Sent for approval. Markets start generating as soon as a teammate approves the series."
          : "Sent for approval. Markets start generating as soon as your tenant approves the series.",
      });
      setForm(EMPTY);
      load();
    } catch (err) {
      setNotice({ tone: "red", text: err.message || "Could not create the series." });
    } finally {
      setSaving(false);
    }
  }

  async function act(item, path) {
    try {
      await apiPost(`/portal/market-series/${item.id}/${path}`, {});
      load();
    } catch (err) {
      setNotice({ tone: "red", text: describeActionError(err) || `Could not ${path} the series.` });
    }
  }

  async function runAdmin(reason) {
    setAdminBusy(true);
    setAdminError(null);
    try {
      await apiPost(`/admin/tenants/${audience.tenantId}/market-series/${pendingAdmin.item.id}/${pendingAdmin.path}`, { reason });
      setNotice({ tone: "green", text: `${pendingAdmin.item.name}: ${ADMIN_ACTIONS[pendingAdmin.path].done}.` });
      setPendingAdmin(null);
      load();
    } catch (err) {
      setAdminError(describeActionError(err));
    } finally {
      setAdminBusy(false);
    }
  }

  const pendingSeries = series.filter((item) => item.status === "PENDING_APPROVAL");

  function renderActions(item) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge label={item.status.replaceAll("_", " ")} tone={STATUS_TONE[item.status] || "gray"} />
        {canApprove && item.status === "PENDING_APPROVAL" && (
          <>
            <button type="button" onClick={() => setPendingAdmin({ item, path: "approve" })} className={`${SMALL_BUTTON} text-green-600`}>
              Approve
            </button>
            <button type="button" onClick={() => setPendingAdmin({ item, path: "reject" })} className={`${SMALL_BUTTON} text-red-500`}>
              Reject
            </button>
          </>
        )}
        {canManage && item.status === "ACTIVE" && (
          <button type="button" onClick={() => act(item, "pause")} className={SMALL_BUTTON}>
            Pause
          </button>
        )}
        {canManage && item.status === "PAUSED" && item.approvedAt && (
          <button type="button" onClick={() => act(item, "resume")} className={SMALL_BUTTON}>
            Resume
          </button>
        )}
        {canArchive && ["ACTIVE", "PAUSED", "REJECTED", "DRAFT"].includes(item.status) && (
          <button type="button" onClick={() => setPendingAdmin({ item, path: "archive" })} className={`${SMALL_BUTTON} text-red-500`}>
            Archive
          </button>
        )}
      </div>
    );
  }

  function renderRow(item) {
    return (
      <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">{item.name}</p>
          <p className="text-xs text-gray-500">
            {cadenceText(item)} · {(item.outcomeSelector?.literals || []).map((o) => o.label).join(", ")}
          </p>
          {audience.isTenant && item.status === "PENDING_APPROVAL" && (
            <p className="mt-1 text-xs text-gray-500">
              {item.questionTemplate} · Result from: {item.resolutionSource}
            </p>
          )}
        </div>
        {renderActions(item)}
      </div>
    );
  }

  const interval = form.cadence === "INTERVAL";

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-navy-700 dark:text-white">Recurring markets</p>
            <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
              Set a question up once and a fresh market is created on schedule: every few minutes, every day, or on chosen days. Each
              answer you list becomes its own Yes/No market.{" "}
              {audience.isTenant
                ? "Your team approves each series first, including those your partners propose, and resolves every market it creates."
                : "Your tenant approves the series first and resolves every market it creates."}
            </p>
          </div>
          <div className="flex gap-2">
            {canManage && (
            <Link to="/portal/markets/new" className="rounded-sm border border-gray-200 px-3 py-2 text-xs font-semibold text-navy-700 dark:border-white/10 dark:text-white">
              One-off market instead
            </Link>
            )}
            <Link to="/portal/guide" className="rounded-sm border border-gray-200 px-3 py-2 text-xs font-semibold text-navy-700 dark:border-white/10 dark:text-white">
              Guide
            </Link>
          </div>
        </div>
      </Card>

      {audience.isTenant && canApprove && pendingSeries.length > 0 && (
        <Card extra="p-5">
          <p className="text-base font-semibold text-navy-700 dark:text-white">Awaiting your approval ({pendingSeries.length})</p>
          <p className="mt-1 text-xs text-gray-500">Series proposed by your partners or your team. Nothing is generated until one is approved.</p>
          <div className="mt-3 divide-y divide-gray-100 dark:divide-white/5">{pendingSeries.map(renderRow)}</div>
        </Card>
      )}

      {notice && (
        <p className={`rounded-xl p-3 text-sm ${notice.tone === "green" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{notice.text}</p>
      )}

      <ConfirmDialog
        key={pendingAdmin ? `${pendingAdmin.item.id}-${pendingAdmin.path}` : "none"}
        open={!!pendingAdmin}
        title={pendingAdmin ? `${ADMIN_ACTIONS[pendingAdmin.path].title} (${pendingAdmin.item.name})` : ""}
        description={pendingAdmin ? ADMIN_ACTIONS[pendingAdmin.path].description : ""}
        confirmLabel={pendingAdmin ? ADMIN_ACTIONS[pendingAdmin.path].label : "Confirm"}
        tone={pendingAdmin ? ADMIN_ACTIONS[pendingAdmin.path].tone : "primary"}
        requireReason
        busy={adminBusy}
        error={adminError}
        onConfirm={runAdmin}
        onCancel={() => {
          setPendingAdmin(null);
          setAdminError(null);
        }}
      />

      <Card extra="p-5">
        <p className="text-base font-semibold text-navy-700 dark:text-white">{audience.isTenant ? "All series" : "Your series"}</p>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        {loading ? (
          <p className="mt-3 text-sm text-gray-500">Loading…</p>
        ) : series.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">{canManage ? "No recurring markets yet. Create one below." : "No recurring markets yet."}</p>
        ) : (
          <div className="mt-3 divide-y divide-gray-100 dark:divide-white/5">{series.map(renderRow)}</div>
        )}
      </Card>

      {canManage && (
      <Card extra="p-5">
        <p className="text-base font-semibold text-navy-700 dark:text-white">New recurring market</p>
        <form onSubmit={submit} className="mt-4 grid gap-4">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {CADENCES.map((c) => (
              <label
                key={c.value}
                className={`cursor-pointer rounded-xl border p-3 ${form.cadence === c.value ? "border-brand-500 bg-brand-500/5" : "border-gray-200 dark:border-white/10"}`}
              >
                <input type="radio" className="sr-only" checked={form.cadence === c.value} onChange={() => set("cadence", c.value)} />
                <p className="text-sm font-semibold text-navy-700 dark:text-white">{c.label}</p>
                <p className="mt-1 text-xs text-gray-500">{c.hint}</p>
              </label>
            ))}
          </div>

          <label className="text-sm font-medium text-navy-700 dark:text-white">
            Name
            <input className={INPUT} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Safaricom 5-minute price" required />
          </label>
          <label className="text-sm font-medium text-navy-700 dark:text-white">
            Question
            <input
              className={INPUT}
              value={form.questionTemplate}
              onChange={(e) => set("questionTemplate", e.target.value)}
              placeholder={interval ? "Will Safaricom trade above KES 30 at {end}?" : "Who will be on the Daily Nation front page on {date}?"}
              required
            />
            <span className="mt-1 block text-xs text-gray-500">
              {"{date}"} becomes the day. {interval ? "{time} and {end} become the window's start and end, {window} both." : ""}
            </span>
          </label>
          <label className="text-sm font-medium text-navy-700 dark:text-white">
            Answers (one per line)
            <textarea className={INPUT} rows={3} value={form.outcomes} onChange={(e) => set("outcomes", e.target.value)} required />
            <span className="mt-1 block text-xs text-gray-500">Each answer is its own Yes/No market. One answer ("Yes") makes a simple Yes/No question.</span>
          </label>

          {interval ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                Each market runs for
                <select className={INPUT} value={form.intervalMinutes} onChange={(e) => set("intervalMinutes", Number(e.target.value))}>
                  {INTERVALS.map((m) => (
                    <option key={m} value={m}>
                      {m < 60 ? `${m} minutes` : "1 hour"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                Result expected (minutes after close)
                <input type="number" min="0" max="1440" className={INPUT} value={form.resolutionDelayMinutes} onChange={(e) => set("resolutionDelayMinutes", e.target.value)} />
              </label>
            </div>
          ) : (
            <>
              {form.cadence === "WEEKLY" && (
                <div className="flex flex-wrap gap-3 text-sm text-navy-700 dark:text-white">
                  {DAYS.map((d, i) => (
                    <label key={d} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={form.daysOfWeek.includes(i)}
                        onChange={(e) => set("daysOfWeek", e.target.checked ? [...form.daysOfWeek, i].sort() : form.daysOfWeek.filter((x) => x !== i))}
                      />
                      {d}
                    </label>
                  ))}
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-4">
                <label className="text-sm font-medium text-navy-700 dark:text-white">
                  Opens
                  <input type="time" className={INPUT} value={form.openTimeLocal} onChange={(e) => set("openTimeLocal", e.target.value)} />
                </label>
                <label className="text-sm font-medium text-navy-700 dark:text-white">
                  Closes
                  <input type="time" className={INPUT} value={form.closeTimeLocal} onChange={(e) => set("closeTimeLocal", e.target.value)} />
                </label>
                <label className="text-sm font-medium text-navy-700 dark:text-white">
                  Result expected
                  <input type="time" className={INPUT} value={form.resolutionTimeLocal} onChange={(e) => set("resolutionTimeLocal", e.target.value)} />
                </label>
                <label className="text-sm font-medium text-navy-700 dark:text-white">
                  Result day offset
                  <input type="number" min="0" max="30" className={INPUT} value={form.resolutionOffsetDays} onChange={(e) => set("resolutionOffsetDays", e.target.value)} />
                </label>
              </div>
            </>
          )}

          <label className="text-sm font-medium text-navy-700 dark:text-white">
            Where the result comes from
            <input className={INPUT} value={form.resolutionSource} onChange={(e) => set("resolutionSource", e.target.value)} placeholder="NSE live price, Safaricom (SCOM)" required />
          </label>
          <label className="text-sm font-medium text-navy-700 dark:text-white">
            Resolution rule
            <textarea
              className={INPUT}
              rows={3}
              value={form.resolutionRule}
              onChange={(e) => set("resolutionRule", e.target.value)}
              placeholder="Resolves Yes if the last traded price at the close of the window is above KES 30.00. Otherwise No."
              required
            />
          </label>
          <label className="text-sm font-medium text-navy-700 dark:text-white">
            Description (optional)
            <textarea className={INPUT} rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </label>

          <div>
            <button type="submit" disabled={saving} className="rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              {saving ? "Activating…" : "Activate"}
            </button>
          </div>
        </form>
      </Card>
      )}
    </div>
  );
}
